// --- React & Redux
import React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
// --- Actions & Hooks
import { useUpdateEngineData } from "./hooks/useUpdateEngineData";
import { useRecursiveTimeout } from "../LicensingJobs/hooks/useRecursiveTimeout";
import { useLoading } from "../../utils/hooks/useLoading";
import { useToasts } from "../../utils/hooks/useToasts";
import { usePermissions } from "../../utils/permissions";
import { useStoredItemsPerPageSync } from "tap/utils/hooks/useStoredItemsPerPageSync.js";
import { PAGINATION_STORAGE_KEYS } from "tap/utils/paginationPreferences.js";
// --- External UI Libraries
import Button from "@dtci/spellbook/Button";
import Typography from "@dtci/spellbook/Typography";
import { Col, Row } from "antd";
// --- Components
import { PaginationBar } from "../../components/PaginationBar";
import ToastNotificationProvider from "../../components/ToastNotifications";
import { UpdateEngineGrid } from "./components/grid";
import { UpdateEngineSearchForm } from "./components/search-form";
import TwoRowsLayout from "./two-row";
// --- Utilities
import { tapApi } from "../../api";
import { getItemRange } from "../../utils/pagination";
import { parseSearchParams, updateSearchParams } from "../../utils/urlParams";
import { exportToCsv } from "../../utils/exportToCsv";
// --- Config
import { EXPORT_ALL_RANGE } from "../../store/updateEngine/constants";
// --- Styles
import "./styles.scss";

// Auto-refresh interval in milliseconds (15 seconds)
const AUTO_REFRESH_INTERVAL = 15000;

/**
 * UpdateEnginePage displays and manages the Update Engine jobs.
 *
 * Features:
 * - Displays a paginated, searchable, and sortable jobs grid.
 * - Allows starting the update engine.
 * - Supports exporting job data to CSV.
 * - Provides refresh and pagination controls.
 *
 * @component
 * @returns {JSX.Element} The rendered update engine management page.
 */
export const UpdateEnginePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { fetchJobs, updateUpdateEngine, itemsPerPage, updateEngineRange } =
    useUpdateEngineData();
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showErrorToast, showSuccessToast } = useToasts();
  const { isTapAdmin, isJobTracker } = usePermissions();
  const {
    totalPages,
    currentPage,
    totalItems,
    pageSizeOptions,
    isJobViewModalOpen,
  } = useSelector((state) => state.updateEngine);

  useStoredItemsPerPageSync({
    storageKey: PAGINATION_STORAGE_KEYS.updateEngine,
    itemsPerPage,
    rangeKey: "updateEngineRange",
    onSync: updateUpdateEngine,
  });

  const handleChange = async (pageNumber) => {
    if (pageNumber === currentPage) return;

    const nextPageRange = getItemRange(pageNumber, itemsPerPage);
    const data = await fetchJobs(nextPageRange);

    if (data) {
      updateUpdateEngine({
        jobs: data?.content,
        totalPages: data?.totalPages,
        currentPage: data?.pageable?.pageNumber,
        updateEngineRange: nextPageRange,
        totalItems: data?.totalElements,
      });
    }
  };

  const handlePageSizeChange = (newSize) => {
    const currentParams = parseSearchParams(location.search);
    updateSearchParams(location, navigate, {
      ...currentParams,
      pageSize: newSize,
    });
  };

  const handleRefresh = () => {
    updateUpdateEngine({ refreshJobs: true });
  };

  // Auto-refresh is paused while the job view modal is open
  useRecursiveTimeout(
    handleRefresh,
    AUTO_REFRESH_INTERVAL,
    !isJobViewModalOpen,
  );

  const handleExport = async () => {
    const data = await fetchJobs(EXPORT_ALL_RANGE);

    if (data) {
      exportToCsv(data?.content, "jobs_update-engine");
    }
  };

  const handleStartUpdateEngine = async () => {
    setLoadingTrue();
    try {
      const data = await tapApi.startUpdateEngine();

      if (data?.code || data?.httpCode || data?.error || isNaN(data)) {
        showErrorToast(
          data?.message ||
            data?.error?.message ||
            "Failed to start update engine.",
        );
        return;
      }

      updateUpdateEngine({
        selectedJob: { id: data },
        isJobViewModalOpen: true,
      });

      showSuccessToast("Update engine started successfully.");
    } catch (error) {
      showErrorToast(error?.message || "Failed to start update engine.");
    } finally {
      setLoadingFalse();
    }
  };

  return (
    <>
      <Row className="update-engine-page-header">
        <Col>
          <Typography variant="T50" className="update-engine-page-title">
            Update Engine
          </Typography>
        </Col>
        <Col className="update-engine-page-header-actions">
          {isTapAdmin && isJobTracker && (
            <Button
              iconType="fa-solid fa-play"
              labelText="start"
              styleType="link"
              onClick={handleStartUpdateEngine}
            >
              Start Update Engine
            </Button>
          )}
          <Button
            iconType="fa-solid fa-arrow-down-to-line"
            labelText="Export"
            styleType="link"
            onClick={handleExport}
            disabled={totalItems === 0}
          >
            Export
          </Button>
          <Button
            iconType="fa-solid fa-rotate"
            labelText="Refresh"
            styleType="link"
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Col>
      </Row>
      <TwoRowsLayout>
        <main data-position="top">
          <UpdateEngineSearchForm />
        </main>
        <main data-position="bottom">
          <UpdateEngineGrid />
        </main>
      </TwoRowsLayout>
      <PaginationBar
        totalPages={totalPages}
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={handleChange}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={pageSizeOptions}
      />
      <ToastNotificationProvider />
    </>
  );
};
